import Maybe from 'graphql/tsutils/Maybe'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormikValues } from 'formik'
import { useRouter } from 'next/router'

import { ArticleQuiz } from '../../graphql/generated/api-graphql'
import { ArticleQuizProps } from '../../components/molecules/ArticleQuiz'
import { wediaImagesTransform } from '../Common/wedia'
import { Icons } from '../../components/atoms/Icon'
import { app } from '../../configuration'

export const useArticleQuiz = (
  t: any,
  data?: Maybe<ArticleQuiz>
): ArticleQuizProps | undefined => {
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [currentAnswer, setCurrentAnswer] = useState<number | null>(null)
  const [currentScore, setCurrentScore] = useState(0)
  const [finishedPanel, setFinishedPanel] = useState(0)
  const [finished, setFinished] = useState<boolean>(false)

  const { asPath } = useRouter()

  const handleSumbit = useCallback(
    (values?: FormikValues) => {
      // get index of the answer sunbmitted
      const answer = values?.answer?.[0]
      const index = answer ? parseInt(answer) : null

      if (index === null) return

      // if the answer is correct
      const correctAnswerIndex = data?.questions[
        currentQuestion
      ].answers.findIndex((answer) => answer.isCorrect === true)

      // add to the score
      if (index === correctAnswerIndex) {
        setCurrentScore(currentScore + 1)
      }

      // store
      setCurrentAnswer(index)
    },
    [currentQuestion, data?.questions, currentScore]
  )

  const handleNext = useCallback(() => {
    if (data?.questions[currentQuestion + 1]) {
      setCurrentAnswer(null)
      setCurrentQuestion(currentQuestion + 1)
    } else {
      console.log('Seems some stuff are missing')
    }
  }, [currentQuestion, data?.questions])

  const handleFinish = useCallback(() => {
    setFinished(true)
  }, [])

  useEffect(() => {
    if (!finished) return

    const resultIndex =
      data?.scores?.findIndex(
        (score) =>
          score &&
          score?.min != undefined &&
          score.min <= currentScore &&
          !!score?.max &&
          score.max >= currentScore
      ) ?? 0

    setFinishedPanel(resultIndex)
  }, [finished, currentScore, data])

  const props: ArticleQuizProps | undefined = useMemo(() => {
    if (!data) return undefined

    return !finished
      ? {
          imageProps: {
            maxWidth: 910,
            width: 764,
            height: 477,
            withHD: true,
            withLazyLoading: false,
            images: wediaImagesTransform(
              data.questions[currentQuestion].picture
            ),
            alt: data?.questions[currentQuestion]?.picture?.alt ?? '',
          },
          count: t('quiz_current_answers', {
            current: currentQuestion + 1,
            total: data.questions.length,
          }),
          question: data.questions[currentQuestion].question,
          answerTitle:
            currentAnswer !== null
              ? currentAnswer ===
                data.questions[currentQuestion].answers.findIndex(
                  (answer) => answer.isCorrect === true
                )
                ? t('quiz_correct')
                : t('quiz_incorrect')
              : undefined,
          answerText:
            currentAnswer !== null
              ? {
                  text:
                    data?.questions?.[currentQuestion]?.answers?.[currentAnswer]
                      ?.description ?? undefined,
                }
              : undefined,
          currentQuestion: currentQuestion + 1,
          formikForm: {
            initialValues: { answer: [] },
            onSubmit: handleSumbit,
            validateOnChange: false,
          },
          formFieldToggleButtonGroupProps: {
            name: 'answer',
            limit: 1,
            options: data.questions[currentQuestion].answers.map(
              (answer, index) => {
                return {
                  label: answer.answer.replace(/(<([^>]+)>)/gi, ''),
                  value: `${index}`,
                }
              }
            ),
          },
          rightAnswer: `${data.questions[currentQuestion].answers.findIndex(
            (answer) => answer.isCorrect === true
          )}`,
          actionButtonProps:
            currentQuestion === data.questions.length - 1
              ? {
                  onClick: () => handleFinish(),
                  label: t('quiz_submit'),
                }
              : {
                  onClick: () => handleNext(),
                  label: t('quiz_next'),
                },
        }
      : {
          imageProps: {
            maxWidth: 910,
            withHD: true,
            withLazyLoading: false,
            images: wediaImagesTransform(data.scores[finishedPanel]?.picture),
            alt: data?.questions[finishedPanel]?.picture?.alt ?? '',
          },
          count: t('quiz_x_good_answers', {
            score: currentScore,
            total: data.questions.length,
          }),
          question: data.scores[finishedPanel]?.title ?? undefined,
          textEnd: data?.scores?.[finishedPanel]?.description
            ? {
                text: data?.scores?.[finishedPanel]?.description ?? '',
              }
            : undefined,
          facebookButtonProps: {
            label: t('quiz_share_fb'),
            iconPosition: 'left',
            iconProps: {
              icon: Icons.socialFacebook,
              width: 24,
              height: 24,
            },
            href: `https://www.facebook.com/sharer/sharer.php?u=${
              app.APP_URL + asPath
            }`,
          },
          twitterButtonProps: {
            label: t('quiz_share_tw'),
            iconPosition: 'left',
            iconProps: {
              icon: Icons.socialX,
              width: 24,
              height: 24,
            },
            href: `https://twitter.com/share?url=${app.APP_URL + asPath}`,
          },
        }
  }, [
    asPath,
    currentAnswer,
    currentQuestion,
    currentScore,
    data,
    finished,
    finishedPanel,
    handleFinish,
    handleNext,
    handleSumbit,
    t,
  ])

  return props
}
